import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Select, InputNumber, Table, Spin, Alert, Typography, Tooltip } from 'antd';
import { UserContext } from '../../Contexts/UserContext';
import { APIrequest } from '../../Helpers.js';
import { parseRules } from '../Projections/Scoring';
import { normalizePlayerName } from './normalizePlayerName';

const { Title } = Typography;
const { Option } = Select;

const HALF_PPR_SCORING = { "QB": { "Passing Yards": 0.04, "Number of Passing TDs": 4, "Rushing Yards": 0.1, "Number of Rushing TDs": 6, "Receptions": 0.5, "Receiving Yards": 0.1, "Number of Receiving TDs": 6, "Pass Interceptions Thrown": -1 }, "RB": { "Passing Yards": 0.04, "Number of Passing TDs": 6, "Rushing Yards": 0.1, "Number of Rushing TDs": 6, "Receptions": 0.5, "Receiving Yards": 0.1, "Number of Receiving TDs": 6, "Pass Interceptions Thrown": -1 }, "WR": { "Passing Yards": 0.05, "Number of Passing TDs": 6, "Rushing Yards": 0.1, "Number of Rushing TDs": 6, "Receptions": 0.5, "Receiving Yards": 0.1, "Number of Receiving TDs": 6, "Pass Interceptions Thrown": 0 }, "TE": { "Passing Yards": 0.05, "Number of Passing TDs": 6, "Rushing Yards": 0.1, "Number of Rushing TDs": 6, "Receptions": 0.5, "Receiving Yards": 0.1, "Number of Receiving TDs": 6, "Pass Interceptions Thrown": 0 } };

const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

// The projections table isn't versioned by real NFL season - every other page
// (Projections.jsx, MyDash.jsx, MyRosters.jsx) fetches it with this same fixed
// batch label regardless of the actual year, so it's kept independent of the
// `year`/`week` state below (which drive the live MFL schedule/props lookup).
const PROJECTIONS_BATCH_YEAR = 2022;

const formatMatchup = (player) => {
  if (!player?.opponent) return '-';
  return player.isHome ? `vs ${player.opponent}` : `@ ${player.opponent}`;
};

const formatSpread = (player) => {
  if (player?.spread == null) return '-';
  return player.spread > 0 ? `+${player.spread}` : `${player.spread}`;
};

const PROP_LABELS = {
  passing_yards: 'Passing Yards',
  passing_touchdowns: 'Passing TDs',
  rushing_yards: 'Rushing Yards',
  rushing_touchdowns: 'Rushing TDs',
  receiving_yards: 'Receiving Yards',
  receiving_receptions: 'Receptions',
  touchdowns: 'Anytime TD'
};

const propTooltipContent = (player) => {
  if (!player.props) return null;
  return (
    <div>
      {Object.entries(player.props).map(([statID, value]) => (
        <div key={statID}>{PROP_LABELS[statID] || statID}: {value.toFixed(2)}</div>
      ))}
    </div>
  );
};

// Difference cell shared by the receptions/rushing-yards/receiving-yards
// stat columns: shows "prop / mine" with the signed gap colored so a big
// mismatch (prop line pricing the player very differently than my own
// projection) stands out at a glance.
const diffCell = (propValue, myValue, decimals) => {
  if (propValue == null && myValue == null) return '-';
  const diff = propValue != null && myValue != null ? propValue - myValue : null;
  return (
    <span>
      {propValue != null ? propValue.toFixed(decimals) : '-'} / {myValue != null ? myValue.toFixed(decimals) : '-'}
      {diff != null && (
        <span style={{ marginLeft: 6, color: diff > 0 ? '#3f8600' : (diff < 0 ? '#cf1322' : undefined), fontWeight: 600 }}>
          ({diff > 0 ? '+' : ''}{diff.toFixed(decimals)})
        </span>
      )}
    </span>
  );
};

// Pulls prop-implied fantasy point projections and raw prop stat lines for
// every fantasy-relevant player for the week (same prop-scoring logic as the
// Lineups page), ranks them by position, and lines up the individual
// receptions/rushing-yards/receiving-yards prop lines against the user's own
// season-long per-game projections (same source as the Projections page) to
// spot players the sportsbook is pricing meaningfully differently.
function PropBets() {
  const { userID } = useContext(UserContext);
  const [year, setYear] = useState(2026);
  const [week, setWeek] = useState(1);
  const [myLeagues, setMyLeagues] = useState([]);
  const [scoringLeague, setScoringLeague] = useState(null);
  const [scoring, setScoring] = useState(HALF_PPR_SCORING);
  const [projections, setProjections] = useState([]);
  const [propPlayers, setPropPlayers] = useState([]);
  const [hasApiKey, setHasApiKey] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    APIrequest(`/api/db/getProjections/${userID}/${PROJECTIONS_BATCH_YEAR}`)
      .then((data) => setProjections(data || []))
      .catch((err) => console.error('Failed to load projections', err));
  }, [userID]);

  useEffect(() => {
    fetch('/api/mfl/myLeagues')
      .then((res) => res.json())
      .then((data) => {
        setMyLeagues([...(data.leagues?.league || []), { league_id: 5, name: 'Half-PPR' }]);
        setScoringLeague(data.leagues?.league?.[0]?.league_id || 5);
      })
      .catch((err) => console.error('Failed to load leagues', err));
  }, []);

  useEffect(() => {
    if (!scoringLeague) return;
    if (scoringLeague === 5) {
      setScoring(HALF_PPR_SCORING);
      return;
    }
    fetch(`/api/mfl/getLeagueRules?L=${scoringLeague}`)
      .then((res) => res.json())
      .then((data) => setScoring(parseRules(data.rules.positionRules)))
      .catch((err) => console.error('Failed to load league rules', err));
  }, [scoringLeague]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const leagueQuery = scoringLeague && scoringLeague !== 5 ? `&leagueID=${scoringLeague}` : '';
    fetch(`/api/props/weeklyProps?datayear=${year}&dataweek=${week}${leagueQuery}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.response) throw new Error(data.error || data.message);
        setPropPlayers(data.response.players || []);
        setHasApiKey(data.response.hasApiKey);
      })
      .catch((err) => setError(err.message || 'Failed to load prop data'))
      .finally(() => setLoading(false));
  }, [year, week, scoringLeague]);

  // Joins prop-implied lines to the user's own projections by normalized
  // player name (decoupled from any ID-space mismatch between data sources),
  // pulling out just the raw receptions/rushing-yards/receiving-yards stats
  // (not points) from each side to diff against each other.
  const merged = useMemo(() => {
    const projByName = new Map();
    projections.forEach((p) => {
      if (p.PlayerName) projByName.set(normalizePlayerName(p.PlayerName), p);
    });
    return propPlayers.map((pp) => {
      const proj = projByName.get(normalizePlayerName(pp.playerName));
      const propReceptions = pp.props?.receiving_receptions ?? null;
      const propRushingYards = pp.props?.rushing_yards ?? null;
      const propReceivingYards = pp.props?.receiving_yards ?? null;
      const myReceptions = proj?.Receptions ?? null;
      const myRushingYards = proj?.RushingYards ?? null;
      const myReceivingYards = proj?.ReceivingYards ?? null;
      const diffMagnitude = [
        propReceptions != null && myReceptions != null ? Math.abs(propReceptions - myReceptions) : 0,
        propRushingYards != null && myRushingYards != null ? Math.abs(propRushingYards - myRushingYards) / 10 : 0,
        propReceivingYards != null && myReceivingYards != null ? Math.abs(propReceivingYards - myReceivingYards) / 10 : 0
      ].reduce((a, b) => a + b, 0);
      return {
        ...pp,
        propReceptions, propRushingYards, propReceivingYards,
        myReceptions, myRushingYards, myReceivingYards,
        diffMagnitude
      };
    });
  }, [propPlayers, projections]);

  const rankedByPosition = useMemo(() => {
    const map = {};
    POSITIONS.forEach((pos) => {
      map[pos] = merged
        .filter((p) => p.position === pos)
        .sort((a, b) => b.propProjectedScore - a.propProjectedScore)
        .map((p, i) => ({ ...p, rank: i + 1 }));
    });
    return map;
  }, [merged]);

  const columns = [
    { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 55 },
    { title: 'Player', dataIndex: 'playerName', key: 'playerName',
      render: (name, row) => (
        <Tooltip title={propTooltipContent(row)}>
          <span style={{ borderBottom: '1px dotted #999', cursor: 'help' }}>{name}</span>
        </Tooltip>
      ) },
    { title: 'Team', dataIndex: 'team', key: 'team', width: 70 },
    { title: 'Opp', key: 'opp', width: 100, render: (_, row) => `${formatMatchup(row)} (${formatSpread(row)})` },
    { title: 'Prop Proj', dataIndex: 'propProjectedScore', key: 'propProjectedScore', width: 90,
      render: (v) => v != null ? v.toFixed(2) : '-' },
    { title: 'Receptions (Prop / Mine)', key: 'receptions', width: 170,
      render: (_, row) => diffCell(row.propReceptions, row.myReceptions, 1) },
    { title: 'Rushing Yds (Prop / Mine)', key: 'rushingYards', width: 180,
      render: (_, row) => diffCell(row.propRushingYards, row.myRushingYards, 0) },
    { title: 'Receiving Yds (Prop / Mine)', key: 'receivingYards', width: 190,
      render: (_, row) => diffCell(row.propReceivingYards, row.myReceivingYards, 0) }
  ];

  const biggestDifferences = merged
    .filter((p) => p.diffMagnitude > 0)
    .slice()
    .sort((a, b) => b.diffMagnitude - a.diffMagnitude)
    .slice(0, 25);

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Prop Bets - Weekly Rankings & Stat Comparison</Title>
      <div className="flexrow" style={{ gap: 16, marginBottom: 16, alignItems: 'center' }}>
        Year:
        <InputNumber min={2020} max={2030} value={year} onChange={setYear} />
        Week:
        <InputNumber min={1} max={18} value={week} onChange={setWeek} />
        Scoring League:
        <Select
          size="small"
          style={{ width: 260 }}
          value={scoringLeague}
          onChange={setScoringLeague}
          optionLabelProp="label"
        >
          {myLeagues.map((l) => (
            <Option key={l.league_id} value={l.league_id} label={l.name}>{l.name}</Option>
          ))}
        </Select>
      </div>

      {!hasApiKey && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="SPORTSGAMEODDS_API_KEY is not set on the server - no player prop data is available."
        />
      )}
      {error && <Alert style={{ marginBottom: 16 }} type="error" showIcon message={error} />}

      {loading ? <Spin /> : (
        <>
          <Title level={4}>Biggest Stat Differences (prop line vs. my projection)</Title>
          <Table
            rowKey="playerID"
            size="small"
            columns={columns.filter((c) => c.key !== 'rank')}
            dataSource={biggestDifferences}
            pagination={false}
            style={{ marginBottom: 32 }}
          />

          {POSITIONS.map((pos) => (
            <div key={pos} style={{ marginBottom: 32 }}>
              <Title level={4}>{pos} Rankings by Prop-Implied Points</Title>
              <Table
                rowKey="playerID"
                size="small"
                columns={columns}
                dataSource={rankedByPosition[pos]}
                pagination={{ pageSize: 15 }}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default PropBets;
